import { ApolloError } from '@apollo/client';
import { MutateResult } from '@vue/apollo-composable';
import { GraphQLFormattedError } from 'graphql';

import { useUserStore } from '@/shared/pinia/modules/user';

export const useCommunityDispatchUtils = () => {
  const userStore = useUserStore();

  // Side-effects we run on specific community errors before re-throwing.
  // Re-throwing (rather than swallowing) is important: callers like
  // PostComposer/CommentComposer rely on the rejection to decide whether
  // to clear their input. Hiding the error from the UI is handled
  // separately by the `error` filters in the consumer composables.
  const runCommunityErrorSideEffects = (err: unknown): void => {
    if (isUserBlockedError(err)) {
      userStore.notifyAccountBlocked();
    }
  };

  const dispatchMutationWithTosRetry = async <TResult>(
    mutationExecutor: () => MutateResult<TResult>
  ) => {
    try {
      return await mutationExecutor();
    } catch (err: unknown) {
      if (isTosNotAcceptedError(err)) {
        const tosStatus =
          await userStore.openAndAwaitTermsOfServiceAgreementModal();
        if (tosStatus) {
          // The retried mutation can still fail (e.g. with USER_BLOCKED for
          // a blocked user who just accepted ToS). Run the same
          // side-effect handler on its error before propagating.
          try {
            return await mutationExecutor();
          } catch (retryErr: unknown) {
            runCommunityErrorSideEffects(retryErr);
            throw retryErr;
          }
        }
      }

      runCommunityErrorSideEffects(err);

      // Always re-throw — consumers depend on the rejection to skip
      // success-path cleanup. The error is hidden from the UI by the
      // `error` filters in the consumer composables.
      throw err;
    }
  };

  return {
    dispatchMutationWithTosRetry
  };
};

export const isTosNotAcceptedError = (err: unknown): boolean =>
  err instanceof ApolloError && hasTosNotAcceptedError(err.graphQLErrors);

export const hasTosNotAcceptedError = (
  errors: readonly GraphQLFormattedError[]
) =>
  errors?.some(
    (e) =>
      e.extensions &&
      e.extensions.code === 'FORBIDDEN' &&
      e.extensions.reason === 'TERMS_NOT_ACCEPTED'
  ) ?? false;

export const isUserBlockedError = (err: unknown): boolean =>
  err instanceof ApolloError && hasUserBlockedError(err.graphQLErrors);

export const hasUserBlockedError = (errors: readonly GraphQLFormattedError[]) =>
  errors?.some(
    (e) =>
      e.extensions &&
      e.extensions.code === 'FORBIDDEN' &&
      e.extensions.reason === 'USER_BLOCKED'
  ) ?? false;
